// @ts-check
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusTrap from 'focus-trap-react';
import './Modal.css';

/**
 * @typedef {Object} ModalProps
 * @property {boolean} isOpen
 * @property {() => void} onClose
 * @property {React.ReactNode} children
 * @property {string} labelledBy - id of the element labelling this dialog (typically Modal.Header's id)
 * @property {boolean} [dismissable] - if false, ESC and backdrop-click are ignored. Default true.
 * @property {'sm' | 'md' | 'lg'} [size]
 */

/**
 * Modal — accessible dialog with compound API.
 *
 * Usage:
 *   <Modal isOpen={open} onClose={close} labelledBy="bind-title">
 *     <Modal.Header id="bind-title">Bind device</Modal.Header>
 *     <Modal.Body>...</Modal.Body>
 *     <Modal.Footer>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button variant="primary" onClick={submit}>Bind</Button>
 *     </Modal.Footer>
 *   </Modal>
 *
 * Behavior:
 *   - Rendered via React portal to document.body (escapes overflow:hidden parents)
 *   - role="dialog" aria-modal="true" aria-labelledby={labelledBy}
 *   - Focus trapped inside until closed (focus-trap-react)
 *   - ESC closes (unless dismissable=false)
 *   - Click on backdrop closes (unless dismissable=false)
 *   - Body scroll locked while open
 *   - Focus returns to invoking element on close (handled by focus-trap-react)
 *
 * @param {ModalProps} props
 */
export function Modal({ isOpen, onClose, children, labelledBy, dismissable = true, size = 'md' }) {
  const dialogRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  // Body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // ESC closes when dismissable.
  useEffect(() => {
    if (!isOpen || !dismissable) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, dismissable, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (!dismissable) return;
    // Close only if click is on backdrop itself, not bubbled from dialog content.
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <FocusTrap
      focusTrapOptions={{
        escapeDeactivates: false, // we handle ESC ourselves above
        clickOutsideDeactivates: false, // we handle backdrop ourselves above
        returnFocusOnDeactivate: true,
        // jsdom has no layout — without this, tabbable can't see buttons/inputs.
        tabbableOptions: { displayCheck: 'none' },
        // Always have something to focus. Falls back to the dialog itself
        // (tabIndex=-1) when no tabbable children exist or during DOM churn
        // from axe traversal in tests.
        fallbackFocus: () => dialogRef.current,
        // Avoid RTL act() warnings — Kieran's S4 gotcha for focus-trap-react in tests.
        checkCanFocusTrap: () => Promise.resolve(),
      }}
    >
      {/*
        Backdrop click-to-dismiss is a pointer-only convenience. Keyboard users
        dismiss via ESC (handled above; focus is trapped inside the dialog), so
        the backdrop intentionally has no keyboard listener or interactive role.
      */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="modal__backdrop"
        onClick={handleBackdropClick}
        data-testid="modal-backdrop"
      >
        <div
          ref={dialogRef}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          className={`modal modal--${size}`}
        >
          {children}
        </div>
      </div>
    </FocusTrap>,
    document.body,
  );
}

/**
 * @param {{ id: string, children: React.ReactNode, className?: string }} props
 */
function ModalHeader({ id, children, className = '' }) {
  return (
    <h2 id={id} className={`modal__header ${className}`.trim()}>
      {children}
    </h2>
  );
}

/**
 * @param {{ children: React.ReactNode, className?: string }} props
 */
function ModalBody({ children, className = '' }) {
  return <div className={`modal__body ${className}`.trim()}>{children}</div>;
}

/**
 * @param {{ children: React.ReactNode, className?: string }} props
 */
function ModalFooter({ children, className = '' }) {
  return <div className={`modal__footer ${className}`.trim()}>{children}</div>;
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
